import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Dimensions, Platform } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { PrivyProvider } from '@privy-io/react-auth';
import { ConvexProvider, ConvexReactClient } from 'convex/react';
import { Ionicons } from '@expo/vector-icons';
import LoginScreen from './screens/LoginScreen.web';
import MapScreen from './screens/MapScreen.web';
import InternalToolScreen from './screens/InternalToolScreen.web';
import { useAuthData, AuthDataProvider } from './hooks/useAuthData';
import { usePrivy } from '@privy-io/react-auth';
import { MomentUploadProvider } from './utils/MomentUploadContext';
import { NavigationProvider, useNavigation } from './utils/NavigationContext';
import { Analytics } from "@vercel/analytics/react";

const isInternalToolPath = () => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  return path === '/internal-tool';
};

// Use the Convex URL from environment or fallback to production URL
const CONVEX_URL = process.env.EXPO_PUBLIC_CONVEX_URL || 'https://charming-mammoth-182.convex.cloud';
const convex = new ConvexReactClient(CONVEX_URL);

// Hook for responsive design
const useResponsive = () => {
  const [dimensions, setDimensions] = useState(Dimensions.get("window"));
  const isDesktop = dimensions.width >= 768;

  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setDimensions(window);
    });

    return () => subscription?.remove();
  }, []);

  return { isDesktop, width: dimensions.width };
};

// Top Navigation Bar Component for Desktop
function TopNavBar() {
  const { isDesktop } = useResponsive();
  const { setActiveTab, setShowDownloadAppModal, setShowProfileModal, setShowWelcomeModal } = useNavigation();
  const { authenticated } = usePrivy();

  if (!isDesktop || Platform.OS !== 'web') {
    return null;
  }

  return (
    <View style={topNavStyles.container}>
      <View style={topNavStyles.content}>
        <View style={topNavStyles.logoContainer}>
          <Text style={topNavStyles.logoText}>Disgo</Text>
        </View>
        
        <View style={topNavStyles.navButtons}>
          <TouchableOpacity
            style={topNavStyles.navButton}
            onPress={() => {
              setShowDownloadAppModal(true);
            }}
            activeOpacity={0.7}
          >
            <Ionicons
              name="people-outline"
              size={20}
              color="#666"
            />
            <Text style={topNavStyles.navButtonText}>Social</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={topNavStyles.navButton}
            onPress={() => {
              setShowDownloadAppModal(true);
            }}
            activeOpacity={0.7}
          >
            <Ionicons
              name="notifications-outline"
              size={20}
              color="#666"
            />
            <Text style={topNavStyles.navButtonText}>Notifications</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={topNavStyles.navButton}
            onPress={() => {
              if (!authenticated) {
                setShowWelcomeModal(true);
              } else {
                setActiveTab("Profile");
                setShowProfileModal(true);
              }
            }}
            activeOpacity={0.7}
          >
            <Ionicons
              name="person-outline"
              size={20}
              color="#666"
            />
            <Text style={topNavStyles.navButtonText}>
              Profile
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const topNavStyles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e8e8e8',
    zIndex: 1000,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 40,
    paddingVertical: 18,
    maxWidth: 1600,
    width: '100%',
    alignSelf: 'center',
  },
  logoContainer: {
    marginRight: 64,
  },
  logoText: {
    fontSize: 26,
    fontWeight: '700',
    color: '#4e2916',
    letterSpacing: -0.5,
  },
  navButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  navButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 10,
    gap: 8,
    minHeight: 44,
  },
  navButtonText: {
    fontSize: 15,
    fontWeight: '500',
    color: '#666',
    marginLeft: 2,
  },
});

function AppContent() {
    const { authenticated, ready, user } = usePrivy();
    const { authData, saveAuthData } = useAuthData();
    const { isDesktop } = useResponsive();

    // Save auth data when user is authenticated
    React.useEffect(() => {
        if (ready && authenticated && user) {
            const newAuthData = {
                privyDid: user?.id,
                phoneNumber: user?.linked_accounts?.find(acc => acc.type === "phone")?.phoneNumber || null,
                email: user?.linked_accounts?.find(acc => acc.type === "email")?.address || null,
                linkedAccounts: user?.linked_accounts || [],
            };

            // Only save if privyDid changed to prevent unnecessary updates
            if (newAuthData.privyDid !== authData?.privyDid) {
                saveAuthData(newAuthData);
            }
        }
    }, [ready, authenticated, user?.id]); // Only depend on user.id, not the whole user object

    if (!ready) {
        return (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F9F5F3' }}>
                <Text style={{ fontSize: 18, color: '#666' }}>Loading...</Text>
            </View>
        );
    }

    if (isInternalToolPath()) {
        return <InternalToolScreen />;
    }

    // Always show MapScreen - it will handle the login modal internally
    // Add top margin on desktop to account for the top nav bar (73px height)
    const topMargin = Platform.OS === 'web' && isDesktop ? 73 : 0;
    
    return (
        <View style={{ flex: 1 }}>
            <TopNavBar />
            <View style={{ flex: 1, marginTop: topMargin }}>
                <MapScreen />
            </View>
        </View>
    );
}

export default function App() {
    return (
      <>
        <SafeAreaProvider>
            <GestureHandlerRootView style={{ flex: 1 }}>
                <PrivyProvider
                    appId="cm4p1rmr004aa66l7j9j3160b"
                    clientId="client-WY5eWTA2arbaPHQfpgzvH7VjfhrVassQo84BSdTf7AW6u"
                >
                    <ConvexProvider client={convex}>
                        <AuthDataProvider>
                            <MomentUploadProvider>
                                <BottomSheetModalProvider>
                                    <NavigationProvider>
                                        <AppContent />
                                    </NavigationProvider>
                                </BottomSheetModalProvider>
                            </MomentUploadProvider>
                        </AuthDataProvider>
                    </ConvexProvider>
                </PrivyProvider>
            </GestureHandlerRootView>
        </SafeAreaProvider>
        <Analytics />
        </>
    );
}

